import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deletePendingNexxenCampaign,
  fetchPendingNexxenCampaigns,
  nexxenConsoleUrl,
  NEXXEN_QUERY_KEY,
  PendingNexxenCampaign
} from './nexxen-campaigns';

// The Nexxen programmatic half of the buy: the PAUSED, $1-capped IOs the agent
// drafted for the plan's Nexxen lines. They live in the Nexxen platform (held,
// non-delivering); this lists them alongside the direct-IO approvals so the buy
// story is one screen — direct lines open as PDF IOs, Nexxen lines link out to
// the Nexxen console. Renders empty until a Nexxen advertiser is configured and
// the agent has drafted IOs under it — nothing here ever sends or sets live.
interface NexxenDraftsPanelProps {
  company: string;
}

const usd = (value: number): string => `$${value.toLocaleString('en-US')}`;

export const NexxenDraftsPanel = ({ company }: NexxenDraftsPanelProps) => {
  const queryClient = useQueryClient();
  const { data: campaigns } = useQuery({
    queryKey: [NEXXEN_QUERY_KEY, company],
    queryFn: () => fetchPendingNexxenCampaigns(company),
    enabled: Boolean(company)
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePendingNexxenCampaign(company, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [NEXXEN_QUERY_KEY, company] })
  });

  return (
    <div className='elevate-nexxen'>
      <div className='elevate-nexxen-head'>
        <h2 className='elevate-nexxen-title'>Nexxen programmatic — paused draft IOs</h2>
        <p className='elevate-nexxen-sub'>
          The plan&apos;s Nexxen lines are drafted as PAUSED insertion orders in the Nexxen
          platform, carrying the plan&apos;s budget — held and non-delivering. Nothing is set live.
        </p>
      </div>
      {!campaigns || campaigns.length === 0 ? (
        <p className='elevate-nexxen-empty'>
          No Nexxen draft IOs yet. They appear here once a Nexxen advertiser is configured for this
          client and the agent drafts the plan&apos;s programmatic lines.
        </p>
      ) : (
        <ul className='elevate-nexxen-list'>
          {campaigns.map(io => (
            <NexxenRow
              key={io.id}
              io={io}
              onDelete={() => deleteMutation.mutate(io.id)}
              deleting={deleteMutation.isPending && deleteMutation.variables === io.id}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

interface NexxenRowProps {
  io: PendingNexxenCampaign;
  onDelete: () => void;
  deleting: boolean;
}

const NexxenRow = ({ io, onDelete, deleting }: NexxenRowProps) => (
  <li className='elevate-nexxen-row'>
    <div className='elevate-nexxen-row-main'>
      <span className='elevate-nexxen-name'>{io.campaign_name}</span>
      <span className='elevate-nexxen-status'>{io.status} · paused</span>
    </div>
    <div className='elevate-nexxen-meta'>
      <span>IO {io.nexxen_io_id}</span>
      <span>{usd(io.media_budget)}</span>
      <span>
        {io.flight_start_date} → {io.flight_end_date}
      </span>
      <a
        className='elevate-nexxen-link'
        href={nexxenConsoleUrl(io)}
        target='_blank'
        rel='noopener noreferrer'
      >
        View in Nexxen ↗
      </a>
      <button
        type='button'
        className='elevate-nexxen-delete'
        onClick={onDelete}
        disabled={deleting}
      >
        {deleting ? 'Deleting…' : 'Delete'}
      </button>
    </div>
  </li>
);
